import { useCallback } from 'react';
import { batch } from 'react-redux';
import { dashboardInstancePopulate } from '../../../redux/slices/dashboards/dashboardInstance';
import { useAppDispatch } from '../../../redux/hooks';
import {
  IDashboardInstanceFieldsFragment,
  LlmSubjectKind,
} from '../../../utils/apollo/graphqlSchema';
import {
  llmClearSubjectSummary,
  llmPopulateSubjectSummary,
} from '../../../redux/slices/llm';
import {
  clearAllAnalysesAction,
  retainAnalysesThunk,
} from '../../../redux/slices/analyses';
import {
  clearAllLoadingAnalysesAction,
  setLoadingAnalysesInStoreAction,
} from '../../../redux/slices/loadingAnalyses';
import getDashboardAnalysesIds from './getDashboardAnalysesIds';

const useUpdateDashboardStore = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    ({
      dashboard,
      lastVisitedDashboardPathname,
      retainAnalyses,
    }: {
      dashboard: IDashboardInstanceFieldsFragment;
      lastVisitedDashboardPathname?: string;
      retainAnalyses?: boolean;
    }) => {
      batch(() => {
        // Each time when we load or update dashboard instance we
        // have to clean-up old data with analyses and then load
        // a fresh one
        dispatch(
          dashboardInstancePopulate({
            dashboard,
            lastVisitedDashboardPathname,
          }),
        );
        if (dashboard.llmSummary) {
          dispatch(
            llmPopulateSubjectSummary({
              subjectId: dashboard.baseId,
              subjectKind: LlmSubjectKind.Dashboard,
              summary: dashboard.llmSummary,
            }),
          );
        } else {
          dispatch(
            llmClearSubjectSummary({
              subjectId: dashboard.baseId,
              subjectKind: LlmSubjectKind.Dashboard,
            }),
          );
        }

        const analysisIds = getDashboardAnalysesIds(dashboard.rows);
        if (retainAnalyses) {
          dispatch(retainAnalysesThunk({ analysisIds }));
        } else {
          dispatch(clearAllAnalysesAction());
          dispatch(clearAllLoadingAnalysesAction());
          dispatch(
            setLoadingAnalysesInStoreAction({
              analysisIds,
              refresh: false,
            }),
          );
        }
      });
    },
    [dispatch],
  );
};

export default useUpdateDashboardStore;
